import { apiService } from "@base/configs";
import httpService from "@base/services/httpService";
import { IHealthInsuranceCard } from "./healthInsurance.interface";
import { ILifeInsurance, ILifeInsuranceProvider } from "./lifeInsurance.interface";

class PatientInsuranceService {
  /** The record's national health insurance (BHYT) card - empty when the record has none. */
  getHealthByPatientCode(patientRecordCode: string, lang: string) {
    return httpService.get<IHealthInsuranceCard | null>(`${apiService.patient.patientInsurance.healthByPatientCode}/${patientRecordCode}/${lang}`);
  }

  /** Reads a BHYT card from the raw text of its QR. Public, and posted as a form. */
  decodeHealthInsurance(raw: string, lang: string) {
    const form = new URLSearchParams();

    form.append("data", raw);
    form.append("lang", lang);

    return httpService.post<IHealthInsuranceCard>(apiService.patient.healthInsurance.decode, form, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    });
  }

  /** Files a decoded BHYT card on a patient record. */
  saveHealthInsurance(card: IHealthInsuranceCard, patientRecordCode: string, lang: string) {
    return httpService.post(`${apiService.patient.patientInsurance.saveHealth}/${lang}`, {
      address: card.address,
      code: card.code,
      dob: card.dob,
      expired: card.expired,
      gender: card.gender,
      name: card.name,
      health_insurance: card.health_insurance,
      health_insurance_benefit: card.health_insurance_benefit,
      patient_record_code: patientRecordCode,
      raw: (card.raw ?? "").trim(),
    });
  }

  getLifeByPatientCode(patientRecordCode: string) {
    return httpService.get<ILifeInsurance[]>(`${apiService.patient.patientInsurance.lifeByPatientCode}/${patientRecordCode}`);
  }

  /** The insurers' plans a health insurance can be filed under. Public: no session needed. */
  getLifeProviders(lang: string) {
    return httpService.get<ILifeInsuranceProvider[]>(`${apiService.patient.patientInsurance.lifeProviders}/${lang}`);
  }

  /** Creates when `id` is 0, updates otherwise - and answers with the saved record. */
  saveLife(body: ILifeInsurance, lang: string) {
    return httpService.post<ILifeInsurance>(`${apiService.patient.patientInsurance.saveLife}/${lang}`, body);
  }

  /** Every photo goes in one request under the same `files` field. */
  uploadLifeFiles(lifeInsuranceId: number | string, files: File[]) {
    const formData = new FormData();

    files.forEach((file) => formData.append("files", file));

    return httpService.post(`${apiService.patient.patientInsurance.uploadLifeFiles}/${lifeInsuranceId}`, formData);
  }

  deleteLife(id: number | string) {
    return httpService.delete(`${apiService.patient.patientInsurance.deleteLife}/${id}`);
  }

  /** `name` is the photo's `object_name`. */
  deleteLifeFile(name: string) {
    return httpService.delete(apiService.patient.patientInsurance.deleteLifeFile, { params: { name } });
  }
}

export default new PatientInsuranceService();
